import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logout } from '@/auth/logout';

/** Iniciales para el avatar de respaldo cuando el usuario no tiene foto en Entra ID. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  if (first === '') return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

/**
 * Bloque de usuario del navbar: foto (Microsoft Graph vía BFF), nombre y botón
 * de cerrar sesión.
 *
 * La foto se pide con fetch (no con <img src>) para que un 404 —usuario sin foto
 * en Entra ID— no ensucie la consola: en ese caso se queda el avatar de iniciales.
 * El Blob se convierte a un data: URL (no blob:) porque la CSP de la página del
 * SPA permite `img-src 'self' data:` pero no `blob:` (nginx del portal); un blob:
 * URL se renderizaría como imagen rota.
 */
export function UserMenu({ name }: { name: string }) {
  const { t } = useTranslation();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let active = true;

    const blobToDataUrl = (blob: Blob) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });

    fetch('/api/me/photo', { credentials: 'include' })
      .then((res) => (res.ok ? res.blob() : null))
      .then(async (blob) => {
        if (!active || !blob) return;
        const dataUrl = await blobToDataUrl(blob);
        if (active) setPhotoUrl(dataUrl);
      })
      .catch(() => {
        /* Sin foto o sin red → se mantiene el avatar de iniciales. */
      });

    return () => {
      active = false;
    };
  }, []);

  const onLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } catch {
      // Si el logout falla, reactivamos el botón para reintentar.
      setLoggingOut(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {photoUrl ? (
        <img src={photoUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
      ) : (
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-appsol-500 text-xs font-semibold text-white"
        >
          {initials(name)}
        </span>
      )}
      <span className="hidden text-sm font-medium text-brand-dark sm:inline">{name}</span>
      <button
        type="button"
        onClick={onLogout}
        disabled={loggingOut}
        className="rounded border border-appsol-900 px-3 py-1 text-sm font-medium text-appsol-900 transition hover:bg-appsol-900 hover:text-white disabled:opacity-60"
      >
        {t('app.logout')}
      </button>
    </div>
  );
}
